import { useRef } from "react";
import { EntitySheet } from "@web/features/detail/EntitySheet.tsx";
import { GlobeCountryPanel } from "@web/features/globe/GlobeCountryPanel.tsx";
import { TrailBar } from "@web/features/browse/TrailBar.tsx";
import { closeDetailSheet } from "@web/features/detail/sheetUrl.ts";
import { useGlobeStore } from "@web/stores/globe.ts";
import { useUiStore, SHEET_WIDTH } from "@web/stores/ui.ts";

/** The right panel: a preserved-width resizable frame around the entity sheet. */
export function DetailSheet() {
  const open = useUiStore((s) => s.sheetOpen);
  const width = useUiStore((s) => s.sheetWidth);
  const setSheetWidth = useUiStore((s) => s.setSheetWidth);
  const countryPanel = useGlobeStore((s) => s.countryPanel);
  const drag = useRef<{ startX: number; startW: number } | null>(null);

  if (!open) return null;

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startW: width };
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    const delta = drag.current.startX - e.clientX;
    setSheetWidth(drag.current.startW + delta);
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    drag.current = null;
  }

  return (
    <aside
      className="relative flex h-full shrink-0 flex-col overflow-hidden rounded-lg bg-panel-elevated"
      style={{ width }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={width}
        aria-valuemin={SHEET_WIDTH.min}
        aria-valuemax={SHEET_WIDTH.max}
        tabIndex={0}
        className="absolute top-0 left-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-accent/40"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") setSheetWidth(width + 16);
          if (e.key === "ArrowRight") setSheetWidth(width - 16);
        }}
      />

      {countryPanel ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <GlobeCountryPanel panel={countryPanel} />
        </div>
      ) : (
        <>
          <TrailBar />
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <EntitySheet onClose={closeDetailSheet} />
          </div>
        </>
      )}
    </aside>
  );
}
